import { createContext, useContext, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { chatSkinAt, type ChatSkin } from './chats';
import './chat-skin.css';

/**
 * What every chat surface shares (2026-10-20): the skin it wears — Sand by
 * day, Night from 20:00 to 06:00 on the person's clock, looked at again every
 * minute (chat-skin.css cross-fades the flip) — and whether the conversations
 * stand beside the Chats list (a desktop window with a list: two panes, the
 * list on screen, so no Back to it and no banner over it).
 */
export interface ChatFrame {
  skin: ChatSkin;
  split: boolean;
}

const ChatFrameContext = createContext<ChatFrame>({ skin: 'sand', split: false });

export function useChatFrame(): ChatFrame {
  return useContext(ChatFrameContext);
}

/** The browser bar's colour per skin (the bar on Sand, the night's ground on Night): dark icons by day, light by night. */
const THEME_COLOR: Record<ChatSkin, string> = { sand: '#fffdf9', night: '#181310' };

/**
 * `active` while a chat surface is on screen; `zone` the person's clock (GET /api/me/chats `clock.zone`), '' for the
 * device's — a guest's, or before the list's first read.
 */
export function ChatFrameProvider({ active, zone, split, children }: {
  active: boolean;
  zone: string;
  split: boolean;
  children: ReactNode;
}): ReactElement {
  const [skin, setSkin] = useState<ChatSkin>(() => (typeof window === 'undefined' ? 'sand' : chatSkinAt(new Date(), zone)));
  useEffect(() => {
    if (!active) return undefined;
    const check = (): void => setSkin(chatSkinAt(new Date(), zone));
    check();
    const timer = window.setInterval(check, 60_000);
    return () => window.clearInterval(timer);
  }, [active, zone]);
  useEffect(() => {
    if (!active) return undefined;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return undefined;
    const before = meta.content;
    meta.content = THEME_COLOR[skin];
    return () => { meta.content = before; };
  }, [active, skin]);
  return <ChatFrameContext.Provider value={{ skin, split }}>{children}</ChatFrameContext.Provider>;
}

/** A window wide enough for two panes (2026-10-20: 1024px and up). */
export function useWideWindow(): boolean {
  const query = '(min-width: 1024px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = (): void => setWide(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

/** A room's other pages (its invitation, the wait after yes, the closed room) inside the chat frame, on the skin. */
export function ChatRoomFrame({ children }: { children: ReactNode }): ReactElement {
  const { skin } = useChatFrame();
  return <div className="chat-room-frame" data-chat-skin={skin}>{children}</div>;
}
